import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useRole } from './RoleContext';

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Function to fetch multimedia that matches keywords in text
const fetchMatchingMultimedia = async (subjectId, text) => {
  try {
    const response = await fetch(
      `${API_BASE}/api/multimedia/by-keywords?subject_id=${subjectId}&text=${encodeURIComponent(text)}`
    );
    if (response.ok) {
      const data = await response.json();
      return data.items || [];
    }
  } catch (err) {
    console.error('Error fetching multimedia:', err);
  }
  return [];
};

export const ChatProvider = ({ children }) => {
  const { role, isProfessor } = useRole();
  
  // State
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  
  // Selected topic for summary feature
  const [selectedTopic, setSelectedTopic] = useState(null);
  
  // Ref for tracking shown multimedia (use ref to avoid stale closure issues)
  const shownMultimediaIdsRef = useRef(new Set());
  
  // Ref for aborting streams
  const abortControllerRef = useRef(null);

  // Fetch subjects on mount
  useEffect(() => {
    fetchSubjects();
  }, []);

  // Reset chat when role changes
  useEffect(() => {
    setCurrentConversation(null);
    setMessages([]);
    if (selectedSubject) {
      fetchConversations();
    }
  }, [role]);

  // Fetch conversations when subject changes
  useEffect(() => {
    if (selectedSubject) {
      fetchConversations();
    }
  }, [selectedSubject, role]);

  const fetchSubjects = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/subjects`);
      if (!response.ok) throw new Error('Error fetching subjects');
      const data = await response.json();
      setSubjects(data);
      // Auto-select "default" subject if none selected
      if (data.length > 0 && !selectedSubject) {
        const defaultSubject = data.find(s => s.id === 'default') || data[0];
        setSelectedSubject(defaultSubject);
      }
    } catch (err) {
      console.error('Error fetching subjects:', err);
      setError('No se pudieron cargar las asignaturas');
    }
  };

  const fetchConversations = async () => {
    if (!selectedSubject) return;
    try {
      const roleParam = isProfessor ? 'teacher' : 'student';
      const response = await fetch(
        `${API_BASE}/api/conversations?role=${roleParam}&subject_id=${selectedSubject.id}`
      );
      if (!response.ok) throw new Error('Error fetching conversations');
      const data = await response.json();
      setConversations(data);
    } catch (err) {
      console.error('Error fetching conversations:', err);
    }
  };

  // Fetch ALL conversations (for history panel showing all agents)
  const fetchAllConversations = async () => {
    try {
      const roleParam = isProfessor ? 'teacher' : 'student';
      const response = await fetch(
        `${API_BASE}/api/conversations?role=${roleParam}`
      );
      if (!response.ok) throw new Error('Error fetching all conversations');
      const data = await response.json();
      return data;
    } catch (err) {
      console.error('Error fetching all conversations:', err);
      return [];
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await fetch(`${API_BASE}/api/conversations/${conversationId}/messages`);
      if (!response.ok) throw new Error('Error fetching messages');
      const data = await response.json();
      setMessages(data);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('No se pudieron cargar los mensajes');
    }
  };

  const selectConversation = useCallback(async (conversation) => {
    setCurrentConversation(conversation);
    await fetchMessages(conversation.id);
  }, []);

  const startNewChat = useCallback(() => {
    setCurrentConversation(null);
    setMessages([]);
    shownMultimediaIdsRef.current = new Set(); // Reset shown images for new conversation
  }, []);

  const sendMessage = useCallback(async (messageText, useStreaming = true, overrideSubjectId = null) => {
    // Use override subject if provided, otherwise use selectedSubject from state
    const effectiveSubject = overrideSubjectId 
      ? subjects.find(s => s.id === overrideSubjectId) || selectedSubject
      : selectedSubject;
    
    if (!messageText.trim() || !effectiveSubject) return;
    
    setError(null);
    const roleParam = isProfessor ? 'teacher' : 'student';
    
    // Generate temp ID once and reuse it
    const tempMessageId = `temp-${Date.now()}`;
    
    // Add user message to UI immediately
    const userMessage = {
      id: tempMessageId,
      role: 'user',
      content: messageText,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    
    if (useStreaming) {
      await sendStreamingMessage(messageText, roleParam, effectiveSubject, tempMessageId);
    } else {
      await sendStandardMessage(messageText, roleParam, effectiveSubject, tempMessageId);
    }
    
    // Refresh conversations list
    fetchConversations();
  }, [selectedSubject, subjects, currentConversation, isProfessor]);

  const sendStandardMessage = async (messageText, roleParam, effectiveSubject, tempMessageId) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/chat/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: roleParam,
          subject_id: effectiveSubject.id,
          conversation_id: currentConversation?.id || null,
          message: messageText
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error sending message');
      }
      
      const data = await response.json();
      
      // Update conversation ID if new
      if (!currentConversation) {
        setCurrentConversation({ id: data.conversation_id });
      }
      
      // Check for matching multimedia
      let matchingMedia = await fetchMatchingMultimedia(effectiveSubject.id, data.assistant_message);
      
      // Filter out images already shown in this conversation
      if (matchingMedia.length > 0) {
        matchingMedia = matchingMedia.filter(item => !shownMultimediaIdsRef.current.has(item.id));
        
        // Track the new images as shown
        if (matchingMedia.length > 0) {
          matchingMedia.forEach(item => shownMultimediaIdsRef.current.add(item.id));
        }
      }
      
      // Add assistant message with multimedia
      const assistantMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: data.assistant_message,
        multimedia: matchingMedia,
        created_at: data.created_at
      };
      setMessages(prev => [...prev, assistantMessage]);
      
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.message);
      // Remove the user message if there was an error (using the stored tempMessageId)
      setMessages(prev => prev.filter(m => m.id !== tempMessageId));
    } finally {
      setIsLoading(false);
    }
  };

  const sendStreamingMessage = async (messageText, roleParam, effectiveSubject, tempMessageId) => {
    setIsStreaming(true);
    
    // Create placeholder for assistant message
    const assistantMessageId = `assistant-${Date.now()}`;
    const assistantMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      multimedia: [], // Array for matching images
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, assistantMessage]);
    
    try {
      abortControllerRef.current = new AbortController();
      
      const response = await fetch(`${API_BASE}/api/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: roleParam,
          subject_id: effectiveSubject.id,
          conversation_id: currentConversation?.id || null,
          message: messageText
        }),
        signal: abortControllerRef.current.signal
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error sending message');
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      
      let ragSources = [];  // Store sources from done event
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'start') {
                // Update conversation ID if new
                if (!currentConversation) {
                  setCurrentConversation({ id: data.conversation_id });
                }
              } else if (data.type === 'chunk') {
                fullContent += data.content;
                // Update message content
                setMessages(prev => prev.map(m => 
                  m.id === assistantMessageId 
                    ? { ...m, content: fullContent }
                    : m
                ));
              } else if (data.type === 'done') {
                // Capture sources from done event
                if (data.sources && data.sources.length > 0) {
                  ragSources = data.sources;
                  // Update message with sources
                  setMessages(prev => prev.map(m => 
                    m.id === assistantMessageId 
                      ? { ...m, sources: ragSources }
                      : m
                  ));
                }
              } else if (data.type === 'error') {
                throw new Error(data.message);
              }
            } catch (parseErr) {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }
      
      // After streaming completes, check for matching multimedia
      if (fullContent && effectiveSubject) {
        const matchingMedia = await fetchMatchingMultimedia(effectiveSubject.id, fullContent);
        if (matchingMedia.length > 0) {
          // Filter out images already shown in this conversation
          const newMedia = matchingMedia.filter(item => !shownMultimediaIdsRef.current.has(item.id));
          
          // Limit to 1 image per response (max 1 per chat message)
          const limitedMedia = newMedia.slice(0, 1);
          
          if (limitedMedia.length > 0) {
            // Track the new images as shown
            limitedMedia.forEach(item => shownMultimediaIdsRef.current.add(item.id));
            
            setMessages(prev => prev.map(m => 
              m.id === assistantMessageId 
                ? { ...m, multimedia: limitedMedia }
                : m
            ));
          }
        }
      }
      
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Stream aborted');
      } else {
        console.error('Streaming error:', err);
        setError(err.message);
        // Remove both the user message and the empty assistant placeholder on error
        setMessages(prev => prev.filter(m => m.id !== tempMessageId && m.id !== assistantMessageId));
      }
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  const stopStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  // Fetch and display a topic summary in the chat
  const showTopicSummary = async (topicId, topicTitle) => {
    if (!topicId) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/topic-summaries/${topicId}`);
      if (response.ok) {
        const data = await response.json();
        
        // Add the summary as a message from the assistant
        const summaryMessage = {
          id: `summary-${Date.now()}`,
          role: 'assistant',
          content: `📚 **Resumen: ${data.title}**\n\n${data.summary}`,
          created_at: new Date().toISOString()
        };
        
        setMessages(prev => [...prev, summaryMessage]);
      }
    } catch (err) {
      console.error('Error fetching topic summary:', err);
    }
  };

  // Clear the selected topic
  const clearSelectedTopic = () => {
    setSelectedTopic(null);
  };

  const value = {
    // State
    subjects,
    selectedSubject,
    conversations,
    currentConversation,
    messages,
    isLoading,
    isStreaming,
    error,
    selectedTopic,
    // Actions
    setSelectedSubject,
    selectConversation,
    startNewChat,
    sendMessage,
    stopStreaming,
    fetchConversations,
    fetchAllConversations,
    setSelectedTopic,
    showTopicSummary,
    clearSelectedTopic
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export default ChatContext;
